/**
 * atmosphereFn — TSL helper nodes for the Hillaire sky pipeline.
 *
 * These are the GPU equivalents of the CPU reference functions in
 * `AtmosphereConfig.ts` and `lutParameterization.ts`. They're composed by
 * the LUT bakers (TransmittanceLUT, SkyViewLUT).
 *
 * All distances inside the shader are in **kilometres**. The caller must
 * convert world positions from meters to km before uploading.
 *
 * Uniforms are passed as a `SkyUniforms` bag so the same TSL `Fn` bodies
 * can be shared across materials without re-declaring dozens of slots.
 */

import {
  vec3, float, exp, max, abs, pow,
  sqrt, uniform,
} from 'three/tsl';
import type { AtmosphereConfig } from '../AtmosphereConfig.js';

export interface SkyUniforms {
  bottomRadius: ReturnType<typeof uniform>;
  topRadius: ReturnType<typeof uniform>;
  rayleighScattering: ReturnType<typeof uniform>;
  rayleighScaleHeight: ReturnType<typeof uniform>;
  mieScattering: ReturnType<typeof uniform>;
  mieExtinction: ReturnType<typeof uniform>;
  mieScaleHeight: ReturnType<typeof uniform>;
  miePhaseG: ReturnType<typeof uniform>;
  /** THE BACKGROUND AEROSOL's share and scale height (AtmosphereConfig.mieBackgroundFraction). */
  mieBackgroundFraction: ReturnType<typeof uniform>;
  mieBackgroundScaleHeight: ReturnType<typeof uniform>;
  /**
   * Whether the density graph carries the background term — decided when the bag is CREATED (a graph is built once):
   * a config without the layer pays no extra exp per density sample (the WebGL2 inline march evaluates ~270 a pixel).
   */
  readonly mieBackground: boolean;
  ozoneExtinction: ReturnType<typeof uniform>;
  ozoneCenterAltitude: ReturnType<typeof uniform>;
  ozoneHalfWidth: ReturnType<typeof uniform>;
  groundAlbedo: ReturnType<typeof uniform>;
}

/**
 * Allocate and initialize the uniforms bag from a config. Returns a bag the
 * caller stores; calls `.value = ...` on individual slots to push updates.
 */
export function createSkyUniforms(cfg: AtmosphereConfig): SkyUniforms {
  return {
    bottomRadius: uniform(cfg.bottomRadius),
    topRadius: uniform(cfg.topRadius),
    rayleighScattering: uniform(vec3(
      cfg.rayleighScattering.x, cfg.rayleighScattering.y, cfg.rayleighScattering.z,
    )),
    rayleighScaleHeight: uniform(cfg.rayleighScaleHeight),
    mieScattering: uniform(vec3(
      cfg.mieScattering.x, cfg.mieScattering.y, cfg.mieScattering.z,
    )),
    mieExtinction: uniform(vec3(
      cfg.mieExtinction.x, cfg.mieExtinction.y, cfg.mieExtinction.z,
    )),
    mieScaleHeight: uniform(cfg.mieScaleHeight),
    miePhaseG: uniform(cfg.miePhaseG),
    mieBackgroundFraction: uniform(cfg.mieBackgroundFraction ?? 0),
    mieBackgroundScaleHeight: uniform(cfg.mieBackgroundScaleHeight ?? 8),
    mieBackground: (cfg.mieBackgroundFraction ?? 0) > 0,
    ozoneExtinction: uniform(vec3(
      cfg.ozoneExtinction.x, cfg.ozoneExtinction.y, cfg.ozoneExtinction.z,
    )),
    ozoneCenterAltitude: uniform(cfg.ozoneCenterAltitude),
    ozoneHalfWidth: uniform(cfg.ozoneHalfWidth),
    groundAlbedo: uniform(vec3(
      cfg.groundAlbedo.x, cfg.groundAlbedo.y, cfg.groundAlbedo.z,
    )),
  };
}

/** Push a new config onto an existing uniforms bag. */
export function updateSkyUniforms(u: SkyUniforms, cfg: AtmosphereConfig): void {
  u.bottomRadius.value = cfg.bottomRadius;
  u.topRadius.value = cfg.topRadius;
  (u.rayleighScattering.value as { set: (x: number, y: number, z: number) => void })
    .set(cfg.rayleighScattering.x, cfg.rayleighScattering.y, cfg.rayleighScattering.z);
  u.rayleighScaleHeight.value = cfg.rayleighScaleHeight;
  (u.mieScattering.value as { set: (x: number, y: number, z: number) => void })
    .set(cfg.mieScattering.x, cfg.mieScattering.y, cfg.mieScattering.z);
  (u.mieExtinction.value as { set: (x: number, y: number, z: number) => void })
    .set(cfg.mieExtinction.x, cfg.mieExtinction.y, cfg.mieExtinction.z);
  u.mieScaleHeight.value = cfg.mieScaleHeight;
  u.miePhaseG.value = cfg.miePhaseG;
  // (a bag created without the layer keeps a graph without it — the value is only read where the graph has the term)
  u.mieBackgroundFraction.value = cfg.mieBackgroundFraction ?? 0;
  u.mieBackgroundScaleHeight.value = cfg.mieBackgroundScaleHeight ?? 8;
  (u.ozoneExtinction.value as { set: (x: number, y: number, z: number) => void })
    .set(cfg.ozoneExtinction.x, cfg.ozoneExtinction.y, cfg.ozoneExtinction.z);
  u.ozoneCenterAltitude.value = cfg.ozoneCenterAltitude;
  u.ozoneHalfWidth.value = cfg.ozoneHalfWidth;
  (u.groundAlbedo.value as { set: (x: number, y: number, z: number) => void })
    .set(cfg.groundAlbedo.x, cfg.groundAlbedo.y, cfg.groundAlbedo.z);
}

/**
 * TSL: compute Rayleigh / Mie / ozone density scalars at altitude `h` (km).
 *
 * Returns a vec3 where x=Rayleigh, y=Mie, z=Ozone. Each is unitless 0..1.
 * Ozone density is a tent: `max(0, 1 - |h - center| / halfWidth)`.
 */
// NOTE: these helpers are PLAIN graph-building functions, not TSL Fn()s.
// The array-destructured Fn calling convention (with the plain SkyUniforms
// object smuggled as an argument) builds fine on the WGSL backend but the
// GLSL (WebGL2 fallback) builder mangles the first element — every WebGL2
// frame threw "altitudeKm.toVar is not a function" and the sky (and with it
// the whole frame) went black on the fallback path (Quest, headless).
// Inlining the graph per call site compiles identically on both backends.
export const atmosphereDensities = (
  [altitudeKm, u]: [ReturnType<typeof float>, SkyUniforms],
): ReturnType<typeof vec3> => {
    // Clamp below-ground altitudes: the inline view march intersects only the
    // TOP shell (rayShellIntersect far root), so downward rays traverse the
    // planet interior with altitudes down to ≈ −2·bottomRadius. Unclamped,
    // exp(−h/H) overflows to Inf there and Inf·exp(−Inf) = NaN — the sky cube's
    // lower faces bake NaN, the PMREM blur smears it into every env mip, and
    // every lit PBR material renders black (the Quest/WebGL "world went dark"
    // bug; the CPU LUT path ground-clips via rayNearIntersect for the same
    // reason). −1 km keeps the guard invisible above ground while bounding the
    // exponent (exp(1/H) ≈ 1.13).
    const h = max(altitudeKm, float(-1)).toVar();
    const rayleigh = exp(h.div(u.rayleighScaleHeight).negate());
    const mieBoundary = exp(h.div(u.mieScaleHeight).negate());
    // THE BACKGROUND AEROSOL (the CPU computeDensities' mirror) — only in a graph whose config carries the layer.
    const mie = u.mieBackground
      ? mieBoundary.mul(float(1).sub(u.mieBackgroundFraction))
        .add(exp(h.div(u.mieBackgroundScaleHeight).negate()).mul(u.mieBackgroundFraction))
      : mieBoundary;
    const tentDist = abs(h.sub(u.ozoneCenterAltitude));
    const ozone = max(float(0), float(1).sub(tentDist.div(u.ozoneHalfWidth)));
    return vec3(rayleigh, mie, ozone) as unknown as ReturnType<typeof vec3>;
};

/**
 * TSL: total extinction coefficient at altitude (km), combining Rayleigh
 * scattering, Mie extinction (scatter + absorption), and ozone absorption.
 *
 * Returns a vec3 km⁻¹.
 */
export const atmosphereExtinction = (
  [altitudeKm, u]: [ReturnType<typeof float>, SkyUniforms],
): ReturnType<typeof vec3> => {
    const d = atmosphereDensities([altitudeKm, u]);
    return u.rayleighScattering.mul(d.x)
      .add(u.mieExtinction.mul(d.y))
      .add(u.ozoneExtinction.mul(d.z)) as unknown as ReturnType<typeof vec3>;
};

/**
 * TSL: Rayleigh phase function.
 *   p_R(µ) = (3 / 16π) * (1 + µ²)
 */
export const rayleighPhaseFn = (
  [cosTheta]: [ReturnType<typeof float>],
): ReturnType<typeof float> => {
    const k = float(3.0 / (16.0 * Math.PI));
    return k.mul(float(1.0).add(cosTheta.mul(cosTheta))) as unknown as ReturnType<typeof float>;
};

/**
 * TSL: Cornette-Shanks Mie phase function — Hillaire's recommendation over
 * plain Henyey-Greenstein.
 *
 *   k = (3/(8π)) * (1 - g²) / (2 + g²)
 *   p_M(µ, g) = k * (1 + µ²) / (1 + g² - 2g·µ)^1.5
 */
export const cornetteShanksPhaseFn = (
  [cosTheta, g]: [ReturnType<typeof float>, ReturnType<typeof float>],
): ReturnType<typeof float> => {
    const g2 = g.mul(g);
    const k = float(3.0 / (8.0 * Math.PI))
      .mul(float(1.0).sub(g2))
      .div(float(2.0).add(g2));
    const denomBase = float(1.0).add(g2).sub(g.mul(cosTheta).mul(2.0));
    const denom = pow(max(denomBase, float(1e-5)), float(1.5));
    return k.mul(float(1.0).add(cosTheta.mul(cosTheta))).div(denom) as unknown as ReturnType<typeof float>;
};

/**
 * TSL: ray-sphere intersection returning the farthest positive parameter
 * for a ray originating INSIDE a spherical shell. Mirrors
 * `rayAtmosphereIntersect` CPU helper.
 *
 * Returns -1 in the degenerate case (ray points AWAY from sphere centre
 * and wouldn't cross it). Callers should treat negative as "no hit".
 */
export const rayShellIntersect = (
  [originRadius, cosViewZenith, sphereRadius]:
    [ReturnType<typeof float>, ReturnType<typeof float>, ReturnType<typeof float>],
): ReturnType<typeof float> => {
    const b = originRadius.mul(cosViewZenith);
    const c = originRadius.mul(originRadius).sub(sphereRadius.mul(sphereRadius));
    const disc = b.mul(b).sub(c);
    // If disc < 0 (shouldn't normally happen for atm top from inside) return 0.
    const discClamped = max(disc, float(0));
    return b.negate().add(sqrt(discClamped)) as unknown as ReturnType<typeof float>;
};
